<template>
  <span
    ref="outerContainerRef"
    v-tooltip="effectiveTooltip"
    class="tw-inline-block"
    :class="outerContainerClass"
    data-testid="ellipsis-container"
    :style="style"
  >
    <span ref="containerRef" class="sf-ellipsis__text" :class="containerClass">
      <slot></slot>
    </span>
  </span>
</template>

<script lang="ts" setup>
import { isBreakpoint } from '@app/shared/predicates'

import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { computed, ref } from 'vue'

import type { ResponsiveBreakpointsType } from '@app/shared/constants'

type ResponsiveLength = {
  [key in ResponsiveBreakpointsType]?: number
}

interface Props {
  height?: number
  length?: number
  responsiveLength?: ResponsiveLength
  tooltip?: string | null
}

const { height, length, responsiveLength, tooltip = '' } = defineProps<Props>()

const outerContainerRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)

const breakpoints = useBreakpoints(breakpointsTailwind)

const outerContainerClass = computed(() => {
  return {
    'tw-overflow-hidden': height,
    'tw-truncate': !height && length,
  }
})

const containerClass = computed(() => {
  return {
    'sf-ellipsis__text--vertical-ellipsis ':
      shouldDisplayVerticalEllipsis.value,
  }
})

const effectiveLength = computed(() => {
  if (!responsiveLength) {
    return length
  }

  const breakpoint = breakpoints.active()

  if (!breakpoint.value || !isBreakpoint(breakpoint.value)) {
    return length
  }

  return responsiveLength[breakpoint.value] ?? length
})

const style = computed(() => {
  return {
    maxHeight: height ? `${height}px` : 'none',
    maxWidth: `${effectiveLength.value}px`,
  }
})

const isLongerThanExpected = computed(() => {
  if (!effectiveLength.value || !containerRef?.value?.offsetWidth) {
    return false
  }

  return containerRef.value.offsetWidth > effectiveLength.value
})

const isTallerThanExpected = computed(() => {
  if (!height || !outerContainerRef.value || !containerRef.value) {
    return false
  }

  return (
    outerContainerRef.value.offsetHeight + 1 < containerRef.value.offsetHeight
  )
})

const shouldDisplayVerticalEllipsis = computed(() => {
  return isTallerThanExpected.value && !isLongerThanExpected.value
})

const verticalEllipsisOffset = computed(() => {
  return shouldDisplayVerticalEllipsis.value ? `${height}px` : 0
})

const effectiveTooltip = computed(() => {
  return (length && isLongerThanExpected.value) ||
    (height && isTallerThanExpected.value)
    ? tooltip
    : ''
})
</script>

<style scoped lang="scss">
.sf-ellipsis__text {
  line-height: inherit;
  font-size: inherit;

  &--vertical-ellipsis {
    display: block;
    position: relative;
    padding: 0 4px 0 0;

    &::after {
      content: '...';
      position: absolute;
      top: v-bind(verticalEllipsisOffset);
      right: 0;
      transform: translateY(-100%);
    }
  }
}
</style>
